import React from 'react';
import { Typography } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
  geneMarker,
  hasClassificationConflict,
  discrepancyReview,
  MARKER_KIND
} from 'src/util/variantPathogenicity';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { CLINICAL_COLOURS } from 'src/theme/palette';
import { classificationText } from './genotypes-menu/classificationText';

// The chart tick and the clinically-confirmed-diagnosis tick in Canvas.jsx —
// the same claim to a reader, so the same green, from the clinical register.
const CONFIRMED_TICK_COLOUR = CLINICAL_COLOURS.clinicallyConfirmed;

/**
 * Key for the variant-classification markers currently drawn on this pedigree.
 *
 * This is a LEGEND — it explains what the markers mean — and it now says so on
 * its face. It previously pooled every variant of every family member and
 * rendered an unattributed "Lab and ClinVar interpretations differ" line, which
 * read as a verdict about the record on screen: a clinician had no way to tell
 * which person or which variant the warning was about, or that the tier shown
 * came from a different member entirely.
 *
 * Disagreements are therefore attributed here, by person and gene. Full
 * per-source detail lives on the record, which is the only surface with room
 * for provenance.
 */

const identify = (member) => {
  if (!member) return null;
  if (member.upn) return `${member.upn}`;
  if (member.pid !== undefined && member.pid !== null) return `${member.pid}`;
  return null;
};

// Walk the family once, collecting the markers actually IN USE and attributing
// every disagreement to the person and gene it came from.
//
// Markers are collected per GENE, because that is the unit the canvas draws
// (one marker per genotype label row, from `geneMarker`). Collecting them per
// VARIANT — as this did — listed markers that never appear on the chart: a
// gene whose variants disagree draws a single "differ" marker, but a variant
// inside it that individually resolves to Pathogenic would still put
// "Pathogenic" in the legend. The legend then read as a list of possible
// values rather than a key to what is on screen, which is the opposite of what
// a legend is for.
//
// Genotypes hidden from the pedigree are excluded for the same reason. Residual
// imprecision, deliberately not papered over: the canvas also suppresses a
// marker when the genotype LABEL is toggled off, and that is view state this
// component does not receive — so a toggled-off label can still contribute a
// legend row.
const collect = (members) => {
  const markersByLabel = new Map();
  const conflicts = [];
  // The reviewed tick is drawn INDEPENDENTLY of the row's primary glyph, so it
  // is keyed independently here too. Keying it off a marker label would lose it
  // exactly when the row shows something else — which is the bug this whole
  // pass exists to fix.
  let anyReviewedTick = false;

  members?.forEach((member) => {
    member?.genotypes?.forEach((genotype) => {
      if (genotype?.displayOnPedigree === false) return;
      const variants = genotype?.variants || [];

      const marker = geneMarker(variants);
      if (marker && !markersByLabel.has(marker.label)) {
        markersByLabel.set(marker.label, marker);
      }
      if (marker?.allDisagreementsReviewed) anyReviewedTick = true;

      // Conflicts stay per-variant: the point of this line is to say WHICH
      // variant of which person disagrees, which a gene-level rollup loses.
      // Reviewed and unreviewed are listed separately — a legend that lumped
      // them together would contradict the tick beside the gene.
      variants.forEach((variant) => {
        if (hasClassificationConflict(variant)) {
          conflicts.push({
            who: identify(member),
            gene: genotype.symbol || genotype.name || variant.gene || null,
            reviewed: discrepancyReview(variant).reviewed
          });
        }
      });
    });
  });

  return {
    markers: [...markersByLabel.values()],
    conflicts,
    anyReviewedTick
  };
};

// What a marker's label says, in the user's language (TGCLOUD-1893). The
// `differ` labels and the bare reviewed one are variantPathogenicity.js's own
// fixed sentences. A tier marker's label is the source's classification, and a
// reviewed one prefixes it: each is translated only when it is classification
// vocabulary, never when it is a source's own wording (classificationText).
const REVIEWED_SUFFIX = ' — clinician-reviewed discrepancy';
const REVIEWED_ALONE = 'Clinician-reviewed discrepancy';
const markerText = (marker, t) => {
  const label = marker.label;
  if (marker.kind === MARKER_KIND.differ) return t(label);
  if (marker.kind === MARKER_KIND.reviewed) {
    if (label === REVIEWED_ALONE) return t(REVIEWED_ALONE);
    if (typeof label === 'string' && label.endsWith(REVIEWED_SUFFIX))
      return t('{{classification}} — clinician-reviewed discrepancy', {
        classification: classificationText(
          label.slice(0, -REVIEWED_SUFFIX.length),
          t
        )
      });
    return label;
  }
  return classificationText(label, t);
};

const describeConflicts = (conflicts) => {
  const seen = new Set();
  const parts = [];
  for (const c of conflicts) {
    const label = [c.who, c.gene].filter(Boolean).join(' ');
    if (!label || seen.has(label)) continue;
    seen.add(label);
    parts.push(label);
  }
  return parts;
};

export default function VariantPathogenicityLegend({ members }) {
  const t = useAutoTranslation();

  const { markers, conflicts, anyReviewedTick } = React.useMemo(
    () => collect(members),
    [members]
  );

  if (markers.length === 0 && conflicts.length === 0) return null;

  const openLabels = describeConflicts(conflicts.filter((c) => !c.reviewed));
  const reviewedLabels = describeConflicts(conflicts.filter((c) => c.reviewed));

  return (
    <div style={{ marginBottom: 10 }}>
      <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
        {t('Variant Pathogenicity')}
      </Typography>
      <Typography
        style={{ fontSize: '0.72rem', opacity: 0.7, marginBottom: 2 }}
      >
        {t('Markers used on this pedigree')}
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginTop: 4
        }}
      >
        {markers.map((marker) => (
          <div
            key={marker.label}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {marker.kind === MARKER_KIND.differ ? (
              <span
                style={{
                  color: marker.color,
                  fontSize: '0.95rem',
                  lineHeight: 1,
                  fontWeight: 700,
                  width: 10,
                  textAlign: 'center',
                  flexShrink: 0
                }}
              >
                ≠
              </span>
            ) : (
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: marker.color,
                  flexShrink: 0
                }}
              />
            )}
            {/* The source's own words — the legend does not paraphrase a
                classification into a tidier tier. */}
            <Typography style={{ fontSize: '0.85rem' }}>
              {markerText(marker, t)}
            </Typography>
          </div>
        ))}

        {anyReviewedTick && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircleOutlinedIcon
              style={{
                fontSize: 13,
                color: CONFIRMED_TICK_COLOUR,
                flexShrink: 0
              }}
            />
            <Typography style={{ fontSize: '0.85rem' }}>
              {t('Classification discrepancy clinically reviewed')}
            </Typography>
          </div>
        )}

        {openLabels.length > 0 && (
          <div style={{ marginTop: 2 }}>
            <Typography style={{ fontSize: '0.8rem' }}>
              {t(
                'Laboratory and ClinVar classifications differ for: {{list}}',
                {
                  list: openLabels.join(', ')
                }
              )}
            </Typography>
            <Typography style={{ fontSize: '0.72rem', opacity: 0.7 }}>
              {t(
                'See the individual record for each source and its provenance'
              )}
            </Typography>
          </div>
        )}

        {reviewedLabels.length > 0 && (
          <div style={{ marginTop: 2 }}>
            <Typography style={{ fontSize: '0.8rem' }}>
              {t(
                'Reviewed by a clinician, both classifications retained: {{list}}',
                { list: reviewedLabels.join(', ') }
              )}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
